import { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

// Generic imports
import _ from 'lodash';
import axios from 'axios';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import isoWeek from 'dayjs/plugin/isoWeek';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import { useOutletContext, useNavigate, useLocation, useParams } from 'react-router-dom';

// MuI Imports
import Box from '@mui/material/Box';
import Select from '@mui/material/Select';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import { grey, green, red, amber } from '@mui/material/colors';
import Checkbox from '@mui/material/Checkbox';
import Alert from '@mui/material/Alert';

// FontAwesome Imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faWarehouse, faPlus, faSquare, faSquareCheck, faSquareXmark, faSquareKanban, faSquareList, 
    faPlane, faShip, faTruck, faXmark, faFileImport, faChevronDown, faTimes, faPeopleGroup, faEnvelope, faFileLines } from '@fortawesome/pro-light-svg-icons';

// Local Imports
import AccountDetails from './AccountsDetail.js';
import Header from '../../Header.js';
import PIPELINE from './AccountFunnelPipeline.js';
import AccountsMainList from './AccountsMain.list.js';
import AccountsMainFilters from './AccountsMain.filters.js';
import AccountsMainKanban from './AccountsMain.kanban.js';
import AccountsMainImport from './AccountsMain.import.js';
import SortButton from '../components/SortButton.js';
import OpportunitiesFilterOwner from '../components/OpportunitiesFilter.owner.js';
import { AudienceDetailsInner } from '../../AudienceDetails.js';
import SendNewEmailDialog from '../components/SendNewEmailDialog.js';
import NewTaskDialog from './NewTaskDialog.js';
import { calcAccountScore, getScoreColor, calcOrgMaxRevenue } from '../utils/accountScore.js';

dayjs.extend(duration)
dayjs.extend(relativeTime)
dayjs.extend(isoWeek)
dayjs.extend(quarterOfYear)

function AccountsMain(props) {
    const __tRender0 = performance.now();
    const { mode = 'accounts' } = props;
    const { accountId } = useParams();
    const { org, accounts, suppliers, opportunities, organization, setAccounts, accesstoken, seaPorts, featureToggle, emailLoginInfo, me, exchangeRates } = useOutletContext();
    const [searchParams] = useSearchParams();
    const filterQuery = searchParams.get('filter');
    const filterStorage = filterQuery ? decodeURIComponent(atob(filterQuery)) : sessionStorage.getItem(`FlowWelcome.${mode}`);
    const [ showCompleted, setShowCompleted ] = useState(false);
    const [ showOverdue, setShowOverdue ] = useState(false);
    const [ taskOwnerFilter, setTaskOwnerFilter ] = useState('me');
    const [ updatesOwnerFilter, setUpdatesOwnerFilter ] = useState('all');
    const [ hideCwUpdates, setHideCwUpdates ] = useState(true);
    const [ showNewTaskDialog, setShowNewTaskDialog ] = useState(false);
    const [ coldExpanded, _setColdExpanded ] = useState(() => sessionStorage.getItem('accounts.cold.expanded') !== 'false');
    const setColdExpanded = (v) => { const val = typeof v === 'function' ? v(coldExpanded) : v; _setColdExpanded(val); sessionStorage.setItem('accounts.cold.expanded', String(val)); };
    const [ coldSortAsc, _setColdSortAsc ] = useState(() => sessionStorage.getItem('accounts.cold.sortAsc') !== 'false');
    const setColdSortAsc = (v) => { const val = typeof v === 'function' ? v(coldSortAsc) : v; _setColdSortAsc(val); sessionStorage.setItem('accounts.cold.sortAsc', String(val)); };
    const [ tasksExpanded, _setTasksExpanded ] = useState(() => localStorage.getItem('accounts.tasks.expanded') !== 'false');
    const setTasksExpanded = (v) => { _setTasksExpanded(v); localStorage.setItem('accounts.tasks.expanded', String(v)); };
    const [ updatesExpanded, _setUpdatesExpanded ] = useState(() => localStorage.getItem('accounts.updates.expanded') !== 'false');
    const setUpdatesExpanded = (v) => { _setUpdatesExpanded(v); localStorage.setItem('accounts.updates.expanded', String(v)); };
    const [ scoreFilter, _setScoreFilter ] = useState(() => sessionStorage.getItem(`AccountsMain.${mode}.scoreFilter`) || 'all');
    const setScoreFilter = (v) => { _setScoreFilter(v); sessionStorage.setItem(`AccountsMain.${mode}.scoreFilter`, v); };
    const [ includeNew, _setIncludeNew ] = useState(() => localStorage.getItem('AccountsMain.includeNew') === 'true');
    const setIncludeNew = (v) => { _setIncludeNew(v); localStorage.setItem('AccountsMain.includeNew', String(v)); };
    const [ navigating, setNavigating ] = useState(false);
    const [ loadingMsg ] = useState(() => [
        'Herding your accounts into neat little columns...',
        'Convincing your data to show up on time...',
        'Asking your accounts nicely to line up...',
        'Brewing coffee for your sales pipeline...',
        'Polishing your kanban cards...',
        'Teaching your accounts to sit, stay, and sort...',
        'Warming up the engines...',
        'Rolling out the red carpet for your accounts...',
    ][Math.floor(Math.random() * 8)]);

    const baseFilter = (() => {
        const base = {
            tags: [], 
            names: [], 
            owners: [], 
            transportationMethods: [], 
            freetext: [],
            incInactive: false,
            advanced: {
                dateCreated: [],
                lastActivity: [],
                sources: [],
                destinations: [],
                potentialProfit: [0, 1000000],
                actualProfit: [0, 1000000]
            }
        };
        try {
            return filterStorage ? JSON.parse(filterStorage) : base;
        } catch (e) {
            return base;
        }
    })();
    const [ filter, setFilter ] = useState((() => {
        const sessionFilter = sessionStorage.getItem(`AccountsMain.${mode}.filter`);
        try {
            return sessionFilter ? JSON.parse(sessionFilter) : baseFilter;
        } catch (e) {
            return baseFilter;
        }
    })());

    useEffect(() => {
        sessionStorage.setItem(`AccountsMain.${mode}.filter`, JSON.stringify(filter));
    }, [filter]);


    const [ view, setView ] = useState(sessionStorage.getItem('AccountsMain.view') || 'list');
    const [ sorting, _setSorting ] = useState(localStorage.getItem(`AccountsMain.${mode}.sorting`) || 'Account Name');
    const setSorting = (v) => { _setSorting(v); localStorage.setItem(`AccountsMain.${mode}.sorting`, v); };
    const [ selected, setSelected ] = useState([]);
    const [ showSendEmail, setShowSendEmail ] = useState(false);
    const [ showCreateAudience, setShowCreateAudience ] = useState(false);
    const [ showImport, setShowImport ] = useState(false);

    // Memoize so the array reference is stable across renders — keeps downstream
    // useMemos (accountScorer etc.) from recomputing on unrelated re-renders.
    const filteredOpportunities = useMemo(() => (opportunities || []).filter(r => r.status !== 'deleted'), [opportunities]);

    // Pre-index opportunities by account id — shared across filter, sort, and cold accounts
    // Skip when viewing a single account detail (accountId is set)
    const oppsByAccountId = useMemo(() => {
        if (accountId) return new Map();
        const map = new Map();
        for (const o of filteredOpportunities) {
            const id = o.account?.id;
            if (!id) continue;
            const list = map.get(id);
            if (list) list.push(o); else map.set(id, [o]);
        }
        return map;
    }, [filteredOpportunities, accountId]);

    function _setView(view) {
        sessionStorage.setItem('AccountsMain.view', view);
        setView(view);
    }

    const data = mode === 'accounts' ? accounts : suppliers;

    const navigate = useNavigate();
    const location = useLocation();

    const filteredAccounts = useMemo(() => {
        if (accountId) return [];
        const __t0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
        let tags = filter.tags;
        if (mode === 'accounts') tags = filter.tags.filter(t => t.label !== 'Supplier');
        const filterSupplier = filter.tags.find(t => t.label === 'Supplier') && mode === 'accounts';
        const supplierUniqueIds = filterSupplier ? new Set((suppliers || []).map(s => s.uniqueId).filter(Boolean)) : null;
        // Lowercase freetext once; freetext check runs LAST as a second pass since it's the
        // expensive predicate (JSON.stringify of every account).
        const freetextLowered = _.compact((filter.freetext || []).map(t => t.trim().toLowerCase()));
        const filteredCheap = (data || []).filter(account => {
            let toFilter = true;
            if (filter.onlyAddressErrors && account.address?.countryCode && account.address?.countryCode !== 'NA') toFilter = false;
            if (supplierUniqueIds && !supplierUniqueIds.has(account.uniqueId)) toFilter = false;
            // Build the tag label list cheaply (drop the prior cloneDeep — we only read labels).
            const accountTags = [...(account.tags || [])];
            for (const u of (account.updates || [])) {
                if (u && u.tags) for (const t of u.tags) accountTags.push(t);
            }
            const hasPipeline = accountTags.some(t => t.label?.startsWith('Pipeline: '));
            if (!hasPipeline) accountTags.push({ label: `Pipeline: ${PIPELINE[0]}` });

            tags.forEach(tag => { if (!accountTags.find(t => t.label?.toLowerCase() === tag.label?.toLowerCase())) toFilter = false; });
            filter.names.forEach(name => { if (account.name.indexOf(name) === -1) toFilter = false; });
            filter.owners.forEach(name => { if ((account.owner || '').indexOf(name) === -1) toFilter = false; });
            if (!filter.incInactive && account.inactive) toFilter = false;

            const lastActivity = _(account.updates).maxBy(o => o.created);
            if (filter.advanced.lastActivity.length === 2) {
                if (!lastActivity) {
                    toFilter = false;
                } else {
                    if (filter.advanced.lastActivity[0] > lastActivity.created) toFilter = false;
                    if (filter.advanced.lastActivity[1] < lastActivity.created) toFilter = false;
                }
            }

            if (!_.isEmpty(filter.transportationMethods)) {
                const op = (oppsByAccountId.get(account.id) || []).map(o => o.transportationMethod);
                if (_.intersection(op, filter.transportationMethods).length === 0) toFilter = false;
            }

            // Period filter — accounts active in the calendar period
            if (filter.createdPeriod) {
                const now = dayjs();
                let start;
                if (filter.createdPeriod === 'week') start = now.startOf('isoWeek');
                else if (filter.createdPeriod === 'past-week') start = now.subtract(1, 'week').startOf('isoWeek');
                else if (filter.createdPeriod === 'month') start = now.startOf('month');
                else if (filter.createdPeriod === 'past-month') start = now.subtract(1, 'month').startOf('month');
                else if (filter.createdPeriod === 'quarter') start = now.startOf('quarter');
                let end;
                if (filter.createdPeriod === 'past-week') end = now.subtract(1, 'week').endOf('isoWeek');
                else if (filter.createdPeriod === 'past-month') end = now.subtract(1, 'month').endOf('month');
                else if (filter.createdPeriod === 'week') end = now.endOf('isoWeek');
                else if (filter.createdPeriod === 'month') end = now.endOf('month');
                else if (filter.createdPeriod === 'quarter') end = now.endOf('quarter');
                else end = now;
                if (start) {
                    const s = start.valueOf();
                    const e = end.valueOf();
                    const createdInPeriod = account.created >= s && account.created <= e;
                    const updatedInPeriod = (account.updates || []).some(u => u.created >= s && u.created <= e);
                    const taskInPeriod = (account.reminders || []).some(r => r.dueDate && new Date(r.dueDate).getTime() >= s && new Date(r.dueDate).getTime() <= e);
                    const oppInPeriod = (oppsByAccountId.get(account.id) || []).some(o => o.created >= s && o.created <= e);
                    if (!createdInPeriod && !updatedInPeriod && !taskInPeriod && !oppInPeriod) toFilter = false;
                }
            }

            // Inactive filter — accounts with no activity in the last N days
            if (filter.inactiveDays) {
                const cutoff = dayjs().subtract(filter.inactiveDays, 'day').valueOf();
                const activityTimestamps = _.compact([
                    ...(account.updates || []).map(u => u.created),
                    ...(account.reminders || []).filter(r => r.status === 'completed').map(r => new Date(r.dueDate).getTime()),
                    ...(oppsByAccountId.get(account.id) || []).map(o => o.created),
                ]);
                const lastActivity = _.max(activityTimestamps) || 0;
                if (lastActivity >= cutoff) toFilter = false;
            }

            // Created by filter
            if (filter.createdBy) {
                const creators = filter.createdBy.split(',');
                if (!creators.includes(account.owner)) toFilter = false;
            }


            // Opportunity status + type filter
            if (filter.oppStatus || filter.oppType) {
                let acctOpps = oppsByAccountId.get(account.id) || [];
                if (filter.oppType === 'one-off') acctOpps = acctOpps.filter(o => o.type === 'one-off' || o.type === 'one-off-email' || o.type === 'one-off-free-text');
                if (filter.oppType === 'contract') acctOpps = acctOpps.filter(o => o.type === 'contract');
                if (filter.oppStatus) {
                    const statusMap = {
                        draft: o => !o.status || o.status === 'draft' || o.status === 'pre-draft',
                        sent: o => o.status === 'sent-to-endcustomer' || o.status === 'sending-to-endcustomer' || o.status === 'opened-by-customer',
                        won: o => o.status === 'completed' || o.status === 'endcustomer-accepted',
                        lost: o => o.status === 'lost' || o.status === 'endcustomer-rejected',
                    };
                    if (!acctOpps.some(statusMap[filter.oppStatus] || (() => false))) toFilter = false;
                } else {
                    if (acctOpps.length === 0) toFilter = false;
                }
            }

            // Last contacted quick filter
            if (filter.lastContactedDays) {
                const days = filter.lastContactedDays;
                const lastActivityTimestamps = _.compact([
                    ...(account.updates || []).filter(u => !u.html?.includes('Account updated in Cargowise')).map(u => u.created),
                    ...(account.reminders || []).filter(r => r.status === 'completed').map(r => new Date(r.dueDate).getTime()),
                ]);
                const acctOpps = oppsByAccountId.get(account.id) || [];
                if (acctOpps.length > 0) lastActivityTimestamps.push(_.maxBy(acctOpps, o => o.created)?.created);
                const lastActivityDate = _.max(lastActivityTimestamps);
                const daysSince = lastActivityDate ? dayjs().startOf('day').diff(dayjs(lastActivityDate).startOf('day'), 'day') : Infinity;
                if (days > 0 && daysSince > days) toFilter = false;  // "Within X days" — hide if older
                if (days < 0 && daysSince < Math.abs(days)) toFilter = false;  // "Not in X+ days" — hide if recent
            }

            return toFilter;
        });

        // Second pass: freetext (the expensive predicate). Only runs over survivors of
        // the cheap pass, and stringifies each account once per term-vector.
        const filtered = freetextLowered.length === 0
            ? filteredCheap
            : filteredCheap.filter(account => {
                const json = JSON.stringify(account).toLowerCase();
                for (const ft of freetextLowered) {
                    if (json.indexOf(ft) === -1) return false;
                }
                return true;
            });
        const __t1 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
        const __dt = __t1 - __t0;
        if (__dt > 50) console.log(`[perf] AccountsMain filter: ${__dt.toFixed(1)}ms in=${(data || []).length} pass1=${filteredCheap.length} out=${filtered.length}`);
        return filtered;
    }, [data, filter, sorting, oppsByAccountId, accountId]);

    // Sort separately so changing sort order doesn't re-run the expensive filter
    const sortedAccounts = useMemo(() => {
        return _.sortBy(filteredAccounts, account => {
            switch (sorting) {
                case 'Account Name': return account.name;
                case 'Phase': {
                    const pipeline = ((account.tags || []).find(t => t.label.startsWith('Pipeline: ')) || { label: `Pipeline: ${PIPELINE[0]}` }).label.substr(10);
                    return PIPELINE.indexOf(pipeline);
                }
                case 'Estimated Value': return 0;
                case 'Last Update (Oldest first)': {
                    const lastUpdate = _((account.updates || []).filter(u => !u.html?.includes('Account updated in Cargowise'))).maxBy(o => o.created) || { created: 0 };
                    return lastUpdate.created;
                }
                case 'Last Update (Latest first)': {
                    const lastUpdate = _((account.updates || []).filter(u => !u.html?.includes('Account updated in Cargowise'))).maxBy(o => o.created) || { created: 0 };
                    return -1 * lastUpdate.created;
                }
                case 'Last Activity': {
                    const timestamps = _.compact([
                        ...(account.updates || []).filter(u => !u.html?.includes('Account updated in Cargowise')).map(u => u.created),
                        ...(oppsByAccountId.get(account.id) || []).map(o => o.created),
                    ]);
                    return -1 * (_.max(timestamps) || 0);
                }
                case 'Oldest Activity': {
                    const timestamps = _.compact([
                        ...(account.updates || []).filter(u => !u.html?.includes('Account updated in Cargowise')).map(u => u.created),
                        ...(oppsByAccountId.get(account.id) || []).map(o => o.created),
                    ]);
                    return _.max(timestamps) || 0;
                }
                case 'Latest Quote': {
                    const latestQuote = _((oppsByAccountId.get(account.id) || [])).filter(o => o.type === 'one-off' || o.type === 'one-off-email' || o.type === 'one-off-free-text').maxBy(o => o.created);
                    return -1 * (latestQuote?.created || 0);
                }
                case 'Oldest Quote': {
                    const oldestQuote = _((oppsByAccountId.get(account.id) || [])).filter(o => o.type === 'one-off' || o.type === 'one-off-email' || o.type === 'one-off-free-text').maxBy(o => o.created);
                    return oldestQuote?.created || Infinity;
                }
                case 'Volume': return 0;
                default:
                    if (sorting.startsWith('Inactive') || sorting.startsWith('Active')) {
                        const timestamps = _.compact([
                            ...(account.updates || []).map(u => u.created),
                            ...(oppsByAccountId.get(account.id) || []).map(o => o.created),
                        ]);
                        return sorting.startsWith('Active') ? -(_.max(timestamps) || 0) : (_.max(timestamps) || 0);
                    }
                    return 0;
            }
        });
    }, [filteredAccounts, sorting, oppsByAccountId]);

    // Accounts page exclusively shows accounts past the 'New' pipeline stage.
    // Accounts default to PIPELINE[0] ('New') when they have no Pipeline tag.
    const accountsNotNew = useMemo(() => {
        if (mode !== 'accounts') return sortedAccounts;
        return sortedAccounts.filter(a => {
            const piplineTag = (a.tags || []).find(t => t.label?.startsWith('Pipeline: '));
            const pipeline = piplineTag ? piplineTag.label.substr(10) : PIPELINE[0];
            return pipeline !== PIPELINE[0];
        });
    }, [sortedAccounts, mode]);

    // Lazy scorer: builds the orgMax + opps-by-account index ON FIRST CALL only.
    // calcOrgMaxRevenue (O(accounts × opps), ~234M ops on JTM) doesn't run unless
    // someone actually asks for a score for at least one account.
    const accountScorer = useMemo(() => {
        if (!data || !filteredOpportunities) return () => null;
        let orgMax = null;
        let oppsByAccount = null;
        const cache = new Map();
        return (account) => {
            if (!account) return null;
            const cached = cache.get(account.id);
            if (cached !== undefined) return cached;
            if (oppsByAccount === null) {
                oppsByAccount = new Map();
                for (const o of filteredOpportunities) {
                    const id = o.account?.id;
                    if (!id) continue;
                    const arr = oppsByAccount.get(id);
                    if (arr) arr.push(o); else oppsByAccount.set(id, [o]);
                }
                orgMax = calcOrgMaxRevenue({ accounts: data, opportunities: filteredOpportunities, exchangeRates, organization: org });
            }
            const acctOpps = oppsByAccount.get(account.id) || [];
            const score = calcAccountScore({ account, opportunities: acctOpps, exchangeRates, orgMaxRevenue: orgMax, organization: org });
            cache.set(account.id, score);
            return score;
        };
    }, [data, filteredOpportunities, exchangeRates, org]);

    // accountScores object is only populated when the score filter is active (since the
    // filter has to scan every account anyway). Otherwise it's empty and child views
    // call accountScorer on-demand for the rows they actually render.
    const accountScores = useMemo(() => {
        if (scoreFilter === 'all') return {};
        const __t0 = performance.now();
        const scores = {};
        for (const account of sortedAccounts) scores[account.id] = accountScorer(account);
        const __dt = performance.now() - __t0;
        if (__dt > 50) console.log(`[perf] AccountsMain accountScores (filter active): ${__dt.toFixed(1)}ms accounts=${sortedAccounts.length}`);
        return scores;
    }, [sortedAccounts, accountScorer, scoreFilter]);

    const scoreFilteredAccounts = useMemo(() => {
        // For accounts mode, the page operates by default on accounts past the 'New'
        // pipeline stage; flipping the "Including 'New'" toggle uses the full list.
        // Suppliers continue to use the full filtered list.
        const hideNew = featureToggle?.['feature.accounts-hide-new'] && mode === 'accounts' && !includeNew;
        const base = hideNew ? accountsNotNew : sortedAccounts;
        if (scoreFilter === 'all') return base;
        return base.filter(a => {
            const s = accountScores[a.id]?.score ?? 0;
            if (scoreFilter === 'hot') return s >= 8;
            if (scoreFilter === 'warm') return s >= 5 && s < 8;
            if (scoreFilter === 'cool') return s >= 2 && s < 5;
            if (scoreFilter === 'cold') return s < 2;
            return true;
        });
    }, [sortedAccounts, accountsNotNew, accountScores, scoreFilter, mode, includeNew]);

    // Count deactivated accounts matching the current search (freetext or name)
    const deactivatedMatchCount = useMemo(() => {
        if (filter.incInactive || !data) return 0;
        const freetextLowered = _.compact((filter.freetext || []).map(t => t.trim().toLowerCase()));
        const names = filter.names || [];
        if (freetextLowered.length === 0 && names.length === 0) return 0;
        return data.filter(a => a.inactive).filter(a => {
            if (names.length > 0 && !names.every(n => (a.name || '').indexOf(n) !== -1)) return false;
            if (freetextLowered.length > 0) {
                const json = JSON.stringify(a).toLowerCase();
                if (!freetextLowered.every(ft => json.indexOf(ft) !== -1)) return false;
            }
            return true;
        }).length;
    }, [data, filter.freetext, filter.names, filter.incInactive]);

    useEffect(() => {
        setSelected(_.intersection(selected, scoreFilteredAccounts.map(a => a.id)));
    }, [scoreFilteredAccounts]);


    if (accountId) {
        return (
            <AccountDetails accountId={accountId} mode={mode} />
        )
    }

    if (!opportunities || !data) return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, gap: 2 }}>
            <CircularProgress size={32} sx={{ color: '#3b82f6' }} />
            <Typography sx={{ fontSize: '13px', color: '#94a3b8', fontWeight: 500, animation: 'pulse 2s ease-in-out infinite' }}>
                {loadingMsg}
            </Typography>
            <style>{`@keyframes pulse { 0%, 100% { opacity: 0.5; } 50% { opacity: 1; } }`}</style>
        </Box>
    );

    function onOpen({ account, subscreen = 'details', newWindow }) {

        if (newWindow && subscreen === 'details') {
            if (!location.pathname || location.pathname.length < 2) {
                window.open(`https://app.monada.ai/organization/${organization.alias}/${mode}/${account.id.toLowerCase()}/details`, '_blank');
            } else {
                window.open(`${location.pathname}/${account.id.toLowerCase()}/details?${location.search}`, '_blank');
            }
            return;
        }

        if (subscreen === 'details') {
            setNavigating(true);
            navigate(`./${encodeURIComponent(account.id.toLowerCase())}/${subscreen}`);
        } else {
            const newFilter = {
                days: 5000,
                taskType: subscreen,
                teamMember: 'all',
                accounts: [{ label: account.name, ids: [ account.id ] }],
                openTasksFilter: [],
                rateRequest: 'all',
                taskStatus: 'all'
            }
            sessionStorage.setItem('FlowWelcome.main.opportunities', JSON.stringify(newFilter));
            navigate('../main');
        }
    }

    function onNewAccount() {
        navigate(`./new/details`);
    }

    function onImportAccount() {
        setShowImport(true);
    }

    async function updateAccount({ account, setFirst, includeOwner }) {
        if (setFirst) {
            setAccounts([...accounts.filter(ac => ac.id !== account.id), account]);
        }

        // `owner` is normally stripped (owner changes go through dedicated flows); keep it when
        // an explicit ownership reassignment asks for it (e.g. from the kanban owner menu).
        let _account = _.omit(account, includeOwner ? ['created', 'log', 'modified'] : ['owner', 'created', 'log', 'modified']);
        _account.updates = _account.updates?.map(u => !u.pendingDelete ? u : {..._.omit(u, 'pendingDelete'), deleted: true });
        _account.updates = _account.updates?.map(u => !u.editHtml ? u : {..._.omit(u, 'editHtml', 'editTags'), html: u.editHtml, tags: u.editTags });
        _account.updates = _account.updates?.map(u => _.omit(u, 'wasEdited'));

        const { data } = await axios.put(`/api/${mode}/${account.id}?accesstoken=${accesstoken}&organizationId=${org.id}`, { [ mode === 'accounts' ? 'account' : 'supplier' ]: _account });
        setAccounts([...accounts.filter(ac => ac.id !== account.id), data]);
    }

    const __tRender1 = performance.now();
    const __dtRender = __tRender1 - __tRender0;
    if (__dtRender > 50) {
        const sinceArrival = window.__accountsArrivedAt ? ` (arrived→render ${(__tRender1 - window.__accountsArrivedAt).toFixed(0)}ms)` : '';
        console.log(`[perf] AccountsMain render body: ${__dtRender.toFixed(1)}ms accounts=${(accounts || []).length} filtered=${filteredAccounts.length}${sinceArrival}`);
    }

    return (
        <Box sx={{ paddingBottom: '36px', background: '#fafbfc', position: 'relative' }}>
            {navigating && (
                <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, backgroundColor: 'rgba(255,255,255,0.7)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                    <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px' }} />
                    <CircularProgress size={28} sx={{ color: '#3b82f6' }} />
                    <Typography sx={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>Opening account...</Typography>
                </Box>
            )}
            <Header
                icon={faWarehouse}
                title={mode === 'accounts' ? 'Accounts' : 'Suppliers'}
                buttons={_.compact([featureToggle['feature.accountimportleads'] ? {
                    title: 'Import Leads',
                    icon: faFileImport,
                    color: 'monada-button',
                    action: onImportAccount,
                } : null, {
                    title: mode === 'accounts' ? 'New Account' : 'New Supplier',
                    icon: faPlus,
                    color: 'monada-button',
                    action: onNewAccount,
                }])}
            />
            <Box sx={{ display: 'flex', alignItems: 'stretch', justifyContent: 'center', marginTop: '12px' }}>
                <Paper sx={{ padding: '20px', margin: '0px 12px', flex: 1, maxWidth: '50%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: tasksExpanded ? 1 : 0 }}>
                        <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#334155', cursor: 'pointer', userSelect: 'none' }} onClick={() => setTasksExpanded(!tasksExpanded)}>
                            {tasksExpanded ? '▾' : '▸'} Tasks {(() => {
                                const allTasks = (data || []).flatMap(ac => (ac.reminders || []).filter(r => r != null));
                                const open = allTasks.filter(r => r.status === 'open');
                                const overdue = open.filter(r => r.dueDate && new Date(r.dueDate) < new Date());
                                return <span style={{ fontWeight: 400, color: '#94a3b8' }}>
                                    ({open.length} open{overdue.length > 0 ? <span style={{ color: red[500] }}>, {overdue.length} overdue</span> : ''})
                                </span>;
                            })()}
                        </Typography>
                        {tasksExpanded && <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Button size='small' onClick={() => setShowNewTaskDialog(true)} sx={{ textTransform: 'none', fontSize: '11px', minWidth: 0 }}>
                                + Add
                            </Button>
                            <Typography sx={{ fontSize: '11px', color: '#e2e8f0' }}>|</Typography>
                            <OpportunitiesFilterOwner
                                org={org}
                                filter={{ members: taskOwnerFilter === 'me' ? [me.email] : taskOwnerFilter === 'all' ? [] : taskOwnerFilter.split(',') }}
                                setFilter={({ members }) => {
                                    if (!members || members.length === 0) setTaskOwnerFilter('all');
                                    else if (members.length === 1 && members[0] === me.email) setTaskOwnerFilter('me');
                                    else setTaskOwnerFilter(members.join(','));
                                }}
                                me={me}
                                noMarginRight={true}
                                mineText='My tasks'
                            />
                            <Typography sx={{ fontSize: '11px', color: '#e2e8f0' }}>|</Typography>
                            <Tooltip title='Tasks that are still open but past their due date' arrow placement='bottom'>
                                <Typography
                                    sx={{ fontSize: '11px', color: showOverdue ? '#64748b' : '#94a3b8', cursor: 'pointer', '&:hover': { color: '#64748b' } }}
                                    onClick={() => setShowOverdue(!showOverdue)}
                                >
                                    {showOverdue ? '✓ Overdue' : 'Overdue'}
                                </Typography>
                            </Tooltip>
                            <Typography sx={{ fontSize: '11px', color: '#e2e8f0' }}>|</Typography>
                            <Tooltip title='Tasks that have been marked as done' arrow placement='bottom'>
                                <Typography
                                    sx={{ fontSize: '11px', color: showCompleted ? '#64748b' : '#94a3b8', cursor: 'pointer', '&:hover': { color: '#64748b' } }}
                                    onClick={() => setShowCompleted(!showCompleted)}
                                >
                                    {showCompleted ? '✓ Completed' : 'Completed'}
                                </Typography>
                            </Tooltip>
                        </Box>}
                    </Box>
                    {tasksExpanded && <Box sx={{ display: 'flex', flexDirection: 'column', maxHeight: '250px', overflowY: 'auto', gap: 0.5 }}>
                        {_.sortBy(
                            (data || []).flatMap(ac => (ac.reminders || []).filter(r => r != null).map(r => ({...r, ac: ac.name, acId: ac.id, accountOwner: ac.owner})))
                                .filter(r => showCompleted || r.status === 'open')
                                .filter(r => {
                                    if (showOverdue) return true;
                                    if (r.status !== 'open') return true;
                                    return !r.dueDate || new Date(r.dueDate) >= new Date();
                                })
                                .filter(r => {
                                    if (taskOwnerFilter === 'all') return true;
                                    if (taskOwnerFilter === 'me') return (r.assignee || r.accountOwner) === me.email;
                                    const members = taskOwnerFilter.split(',');
                                    return members.includes(r.assignee || r.accountOwner);
                                }),
                            r => r.status === 'open' ? 0 : 1, r => new Date(r.dueDate).getTime()
                        ).map(reminder => {
                            const isOverdue = reminder.status === 'open' && reminder.dueDate && new Date(reminder.dueDate) < new Date();
                            const isDone = reminder.status === 'completed' || reminder.status === 'rejected';
                            return (
                                <Box
                                    key={reminder.id}
                                    sx={{
                                        display: 'flex', alignItems: 'center', gap: 0.5, py: 0.5, px: 1,
                                        borderRadius: '6px',
                                        backgroundColor: isOverdue ? red[50] : 'transparent',
                                        '&:hover': { backgroundColor: isOverdue ? red[50] : grey[50] },
                                        cursor: 'pointer',
                                    }}
                                >
                                    <Checkbox
                                        size='small'
                                        checked={isDone}
                                        sx={{ p: 0.5, color: isOverdue ? red[400] : grey[400] }}
                                        onClick={async (e) => {
                                            e.stopPropagation();
                                            const account = data.find(a => a.id === reminder.acId);
                                            if (!account) return;
                                            const newReminders = (account.reminders || []).map(r =>
                                                r.id === reminder.id ? { ...r, status: r.status === 'open' ? 'completed' : 'open' } : r
                                            );
                                            await updateAccount({ account: { ...account, reminders: newReminders } });
                                        }}
                                    />
                                    <Box sx={{ flex: 1, minWidth: 0 }} onClick={() => onOpen({ account: { id: reminder.acId }})}>
                                        <Typography sx={{
                                            fontSize: '12px', fontWeight: 500,
                                            textDecoration: isDone ? 'line-through' : 'none',
                                            color: isDone ? grey[400] : '#334155',
                                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                        }}>
                                            {reminder.description}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                            <Typography sx={{ fontSize: '10px', color: isOverdue ? red[600] : '#94a3b8', fontWeight: isOverdue ? 600 : 400 }}>
                                                {isOverdue ? 'Overdue · ' : ''}{dayjs(reminder.dueDate).format('MMM D, h:mm A')}
                                            </Typography>
                                            <Typography sx={{ fontSize: '10px', color: '#94a3b8' }}>
                                                · {reminder.ac}
                                            </Typography>
                                            {reminder.assignee && (
                                                <Typography sx={{ fontSize: '10px', color: '#94a3b8' }}>
                                                    → {reminder.assignee.split('@')[0]}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>}
                </Paper>
                <Paper sx={{ padding: '20px', margin: '0px 12px', flex: 1, maxWidth: '50%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: updatesExpanded ? 1 : 0 }}>
                        <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#334155', cursor: 'pointer', userSelect: 'none' }} onClick={() => setUpdatesExpanded(!updatesExpanded)}>
                            {updatesExpanded ? '▾' : '▸'} Latest Updates
                        </Typography>
                        {updatesExpanded && <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <OpportunitiesFilterOwner
                                org={org}
                                filter={{ members: updatesOwnerFilter === 'all' ? [] : updatesOwnerFilter === 'me' ? [me.email] : updatesOwnerFilter.split(',') }}
                                setFilter={({ members }) => {
                                    if (!members || members.length === 0) setUpdatesOwnerFilter('all');
                                    else if (members.length === 1 && members[0] === me.email) setUpdatesOwnerFilter('me');
                                    else setUpdatesOwnerFilter(members.join(','));
                                }}
                                me={me}
                                noMarginRight={true}
                                mineText='My updates'
                            />
                            {org?.integrations?.cargowise?.enabled && <>
                            <Typography sx={{ fontSize: '11px', color: '#e2e8f0' }}>|</Typography>
                            <Tooltip title='Hide automatic updates synced from CargoWise' arrow placement='bottom'>
                                <Typography
                                    sx={{ fontSize: '11px', color: hideCwUpdates ? '#64748b' : '#94a3b8', cursor: 'pointer', '&:hover': { color: '#64748b' }, whiteSpace: 'nowrap' }}
                                    onClick={() => setHideCwUpdates(!hideCwUpdates)}
                                >
                                    {hideCwUpdates ? '✓ Hide CW' : 'Hide CW'}
                                </Typography>
                            </Tooltip>
                            </>}
                        </Box>}
                    </Box>
                    {updatesExpanded && <Box sx={{ display: 'flex', maxHeight: '250px', overflowY: 'auto', flexDirection: 'column', gap: 0.5 }}>
                        {_.sortBy((data)
                            .map(ac => (ac.updates || []).map(r => ({ ...r, ac: ac.name, acId: ac.id, accountOwner: ac.owner })))
                            .flat()
                            .filter(r => r.status !== 'rejected')
                            .filter(r => !hideCwUpdates || !r.html?.includes('Account updated in Cargowise'))
                            .filter(r => {
                                if (updatesOwnerFilter === 'all') return true;
                                if (updatesOwnerFilter === 'me') return r.createdBy === me.email;
                                const members = updatesOwnerFilter.split(',');
                                return members.includes(r.createdBy);
                            }), r => -1 * new Date(r.created).getTime())
                            .slice(0, 50)
                            .map(update => (
                                <Box
                                    key={update.created}
                                    sx={{ display: 'flex', py: 0.5, px: 1, borderRadius: '4px', cursor: 'pointer', '&:hover': { backgroundColor: grey[50] } }}
                                    onClick={() => onOpen({ account: { id: update.acId } })}
                                >
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Box sx={{ fontSize: '12px', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#334155', lineHeight: 1.4, '& p': { m: 0 }, '& h1, & h2, & h3': { fontSize: '12px', m: 0 }, '& div': { display: 'inline' }, '& br': { display: 'none' } }} dangerouslySetInnerHTML={{ __html: update.html }} />
                                        <Typography sx={{ fontSize: '10px', color: '#94a3b8' }}>
                                            {update.ac} · {dayjs(update.created).fromNow()} · {(update.createdBy || '').split('@')[0]}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                    </Box>}
                </Paper>
            </Box>

            <Box style={{ padding: '12px', paddingTop: '24px' }}>
                <AccountsMainFilters
                     mode={mode}
                     filter={filter}
                     setFilter={setFilter}
                     accounts={data}
                     seaPorts={seaPorts}
                     featureToggle={featureToggle}
                     org={org}
                     me={me}
                     includeNew={includeNew}
                     setIncludeNew={setIncludeNew}
                />
            </Box>

            <Box style={{ padding: '12px', paddingTop: '0px' }}>

                {showImport && (
                    <AccountsMainImport accounts={accounts} open={showImport} onClose={() => setShowImport(false)} />
                )}

                <>
                    {(() => {
                        const CW1_MARKERS = ['Account updated in Cargowise', 'Account created in TMS'];
                        const isCw1 = u => u?.createdBy === 'cargowise@monada.ai' || (typeof u?.html === 'string' && CW1_MARKERS.some(m => u.html.includes(m)));
                        const coldAccounts = scoreFilteredAccounts.filter(a => {
                            const nonCwUpdates = (a.updates || []).filter(u => !isCw1(u) && !u.deleted);
                            const acctOpps = (filteredOpportunities || []).filter(o => o.account?.id === a.id);
                            const sentOpps = acctOpps.filter(o => o.status && o.status !== 'draft' && o.status !== 'pre-draft');
                            const lastUserActivity = _.max(_.compact([
                                ...nonCwUpdates.map(u => u.created),
                                ...sentOpps.map(o => o.created),
                                ...sentOpps.flatMap(o => (o.log || []).filter(e => e.timestamp && (e.type === 'status' || e.type === 'pipeline')).map(e => e.timestamp)),
                            ]));
                            if (!lastUserActivity) return false;
                            const daysSince = dayjs().startOf('day').diff(dayjs(lastUserActivity).startOf('day'), 'day');
                            return daysSince > 30;
                        });
                        if (coldAccounts.length === 0) return null;
                        const withDays = coldAccounts.map(a => {
                            const nonCwUpdates = (a.updates || []).filter(u => !isCw1(u) && !u.deleted);
                            const acctOpps = (filteredOpportunities || []).filter(o => o.account?.id === a.id).filter(o => o.status && o.status !== 'draft' && o.status !== 'pre-draft');
                            const last = _.max(_.compact([...nonCwUpdates.map(u => u.created), ...acctOpps.map(o => o.created), ...acctOpps.flatMap(o => (o.log || []).filter(e => e.timestamp && (e.type === 'status' || e.type === 'pipeline')).map(e => e.timestamp))]));
                            const daysSince = last ? dayjs().startOf('day').diff(dayjs(last).startOf('day'), 'day') : 999;
                            return { ...a, _lastActivity: last, _daysSince: daysSince };
                        });
                        const sorted = _.sortBy(withDays, a => coldSortAsc ? a._daysSince : -a._daysSince);
                        return (
                            <Paper sx={{ mt: '12px', border: `1px solid ${amber[200]}`, background: amber[50], borderRadius: '8px', overflow: 'hidden' }} elevation={0}>
                                <Box
                                    onClick={() => setColdExpanded(e => !e)}
                                    sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', '&:hover': { background: amber[100] } }}
                                >
                                    <Box>
                                        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: amber[900] }}>
                                            {coldAccounts.length} {coldAccounts.length === 1 ? `${mode === 'accounts' ? 'account is' : 'supplier is'}` : `${mode === 'accounts' ? 'accounts are' : 'suppliers are'}`} going cold
                                        </Typography>
                                        {coldExpanded && (
                                            <Typography sx={{ fontSize: '11px', color: amber[800] }}>
                                                Had engagement but no contact in 30+ days — consider reaching out
                                            </Typography>
                                        )}
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {coldExpanded && (
                                            <Typography
                                                sx={{ fontSize: '11px', color: amber[700], cursor: 'pointer', '&:hover': { color: amber[900] } }}
                                                onClick={(e) => { e.stopPropagation(); setColdSortAsc(s => !s); }}
                                            >
                                                {coldSortAsc ? 'Recent first ↑' : 'Oldest first ↓'}
                                            </Typography>
                                        )}
                                        <Typography sx={{ fontSize: '11px', color: amber[700] }}>{coldExpanded ? '▲ Collapse' : '▼ Expand'}</Typography>
                                    </Box>
                                </Box>
                                {coldExpanded && <Box sx={{ maxHeight: '200px', overflowY: 'auto' }}>
                                    {sorted.map(a => {
                                        const urgencyColor = a._daysSince > 60 ? red[600] : a._daysSince > 45 ? amber[800] : amber[600];
                                        return (
                                            <Box key={a.id}
                                                onClick={() => onOpen({ account: a })}
                                                sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1, cursor: 'pointer', borderTop: `1px solid ${amber[100]}`, '&:hover': { background: amber[100] } }}
                                            >
                                                <Tooltip title={a._daysSince < 999 ? `No activity for ${a._daysSince} days` : 'No user activity recorded'} arrow>
                                                    <Typography sx={{ fontSize: '12px', fontWeight: 700, color: urgencyColor, minWidth: '45px' }}>{a._daysSince < 999 ? `${a._daysSince}d` : '—'}</Typography>
                                                </Tooltip>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography sx={{ fontSize: '12px', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {a.name}
                                                    </Typography>
                                                </Box>
                                                {a.owner && (
                                                    <Typography sx={{ fontSize: '10px', color: '#94a3b8' }}>
                                                        {a.owner.split('@')[0]}
                                                    </Typography>
                                                )}
                                            </Box>
                                        );
                                    })}
                                </Box>}
                            </Paper>
                        );
                    })()}
                    <Paper sx={{ marginTop: '12px', border: `1px solid ${grey[300]}`, padding: '12px' }} elevation={0}>
                        <Typography variant='body2' sx={{ fontWeight: 800, mb: '4px' }}>{mode === 'accounts' ? 'Accounts' : 'Suppliers'}</Typography>
                        <Typography variant='caption' sx={{ fontWeight: 500 }}>
                            <b>{scoreFilteredAccounts.length}</b> {scoreFilteredAccounts.length === 1 ? (mode === 'suppliers' ? 'supplier' : 'account') : (mode === 'suppliers' ? 'suppliers' : 'accounts')}{(() => {
                                const baseline = (data || []).filter(a => filter.incInactive || !a.inactive).length;
                                return scoreFilteredAccounts.length < baseline ? ` (of ${baseline})` : '';
                            })()}
                            {(() => {
                                const acctIds = new Set(scoreFilteredAccounts.map(a => a.id));
                                const acctOpps = (filteredOpportunities || []).filter(o => acctIds.has(o.account?.id));
                                const totalOpps = acctOpps.length;
                                const wonOpps = acctOpps.filter(o => o.status === 'completed' || o.status === 'endcustomer-accepted').length;
                                const openOpps = acctOpps.filter(o => o.status === 'sent-to-endcustomer' || o.status === 'opened-by-customer').length;
                                const openTasks = scoreFilteredAccounts.reduce((sum, ac) => sum + (ac.reminders || []).filter(r => r?.status === 'open').length, 0);
                                const overdueTasks = scoreFilteredAccounts.reduce((sum, ac) => sum + (ac.reminders || []).filter(r => r?.status === 'open' && r.dueDate && new Date(r.dueDate) < new Date()).length, 0);
                                return <> | <b>{totalOpps}</b> opportunities ({wonOpps} won, {openOpps} pending) | <b>{openTasks}</b> open tasks{overdueTasks > 0 ? <span style={{ color: red[500] }}> ({overdueTasks} overdue)</span> : ''}</>;
                            })()}
                        </Typography>
                    </Paper>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '12px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                            <Typography variant='caption' component={Box} sx={{ opacity: 0.5 }}>
                                Showing {scoreFilteredAccounts.length === 1 ? 'one result' : `${scoreFilteredAccounts.length} results`}
                            </Typography>
                            {view === 'list' && (
                                <>
                                    <Typography variant='caption' component={Box} sx={{ opacity: 0.5, marginLeft: '3px' }}>
                                        ({selected.length} selected)
                                    </Typography>
                                    <Button
                                        variant='text' color='primary' sx={{ marginLeft: '6px', fontSize: '11px !important', padding: '0px', minWidth: '0px', textTransform: 'none' }}
                                        onClick={() => {
                                            if (_.isEqual(selected, scoreFilteredAccounts.map(a => a.id))) setSelected([]); else setSelected(scoreFilteredAccounts.map(a => a.id));
                                        }}
                                    >
                                        {_.isEqual(selected, scoreFilteredAccounts.map(a => a.id)) ? 'Deselect all' : 'Select all'}
                                    </Button>
                                </>
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                            {(featureToggle['feature.account-email']) && (
                                <Button color='primary' variant='contained' disabled={selected.length === 0} onClick={() => setShowSendEmail(true)} size='small'>
                                    <FontAwesomeIcon icon={faEnvelope} />&nbsp;
                                    Send Email
                                </Button>
                            )}
                            {(featureToggle['feature.campaigns-and-audiences']) && (
                                <Button color='primary' variant='contained' disabled={selected.length === 0} onClick={() => setShowCreateAudience(true)} size='small'>
                                    <FontAwesomeIcon icon={faPeopleGroup} />&nbsp;
                                    New Audience
                                </Button>
                            )}
                            <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                {[
                                    { key: 'Account Name', label: 'Name', tooltip: 'Sort alphabetically by account name' },
                                    { key: 'Phase', label: 'Phase', tooltip: 'Sort by pipeline stage' },
                                    { key: 'Last Activity', label: 'Activity', altKey: 'Oldest Activity', tooltip: `Sort by most recent activity — includes timeline updates, sent quotes/contracts, and completed tasks${org?.integrations?.cargowise?.enabled ? ' (excludes drafts and CargoWise syncs)' : ' (excludes drafts)'}` },
                                    { key: 'Latest Quote', label: 'Quote', altKey: 'Oldest Quote', tooltip: 'Toggle between newest / oldest quote' },
                                ].map(opt => {
                                    const isActive = sorting === opt.key || sorting === opt.altKey;
                                    const isAlt = sorting === opt.altKey;
                                    return (
                                        <Tooltip key={opt.key} title={opt.tooltip} placement='top' arrow>
                                            <Typography
                                                onClick={() => {
                                                    if (opt.altKey) setSorting(sorting === opt.key ? opt.altKey : opt.key);
                                                    else setSorting(opt.key);
                                                }}
                                                sx={{
                                                    cursor: 'pointer', fontSize: '11px', userSelect: 'none',
                                                    fontWeight: isActive ? 700 : 500,
                                                    color: isActive ? '#1976d2' : grey[500],
                                                    '&:hover': { color: '#1976d2' },
                                                }}
                                            >
                                                {opt.label} {isActive ? (isAlt ? '↑' : '↓') : ''}
                                            </Typography>
                                        </Tooltip>
                                    );
                                })}
                                <Tooltip title='Filter accounts by Monada Score' placement='top' arrow>
                                    <Select
                                        size='small'
                                        value={scoreFilter}
                                        onChange={e => setScoreFilter(e.target.value)}
                                        sx={{ fontSize: '11px', borderRadius: '6px', '& .MuiSelect-select': { py: '2px', px: '8px' }, color: scoreFilter !== 'all' ? '#1976d2' : grey[500], fontWeight: scoreFilter !== 'all' ? 700 : 500 }}
                                    >
                                        <MenuItem value='all'>All Scores</MenuItem>
                                        <MenuItem value='hot'><Box component='span' sx={{ color: '#16a34a', fontWeight: 600 }}>Hot</Box>&nbsp;(8-10)</MenuItem>
                                        <MenuItem value='warm'><Box component='span' sx={{ color: '#d97706', fontWeight: 600 }}>Warm</Box>&nbsp;(5-7)</MenuItem>
                                        <MenuItem value='cool'><Box component='span' sx={{ color: '#ea580c', fontWeight: 600 }}>Cool</Box>&nbsp;(2-4)</MenuItem>
                                        <MenuItem value='cold'><Box component='span' sx={{ color: '#dc2626', fontWeight: 600 }}>Cold</Box>&nbsp;(0-1)</MenuItem>
                                    </Select>
                                </Tooltip>
                            </Box>
                            <Button
                                variant='text'
                                color='primary'
                                onClick={() => _setView('list')}
                                sx={{ minWidth: 0, padding: 0, textTransform: 'none', fontSize: '11px !important', fontWeight: view === 'list' ? 800 : 400}}
                            >
                                <FontAwesomeIcon icon={faSquareList} />&nbsp;List
                            </Button>
                            <Typography variant='caption' component={Box} sx={{ padding: '0px 3px', fontSize: '11px' }}>
                            /
                            </Typography>
                            <Button
                                variant='text'
                                color='primary'
                                onClick={() => _setView('kanban')}
                                sx={{ minWidth: 0, padding: 0, textTransform: 'none', fontSize: '11px !important', fontWeight: view === 'kanban' ? 800 : 400}}
                            >
                                <FontAwesomeIcon icon={faSquareKanban} />&nbsp;Kanban
                            </Button>
                        </Box>
                    </Box>
                    </>

                {scoreFilteredAccounts.length === 0 && deactivatedMatchCount > 0 && (
                    <Alert severity='info' sx={{ mt: 1, mb: 1 }}>
                        No results found, but {deactivatedMatchCount} deactivated {deactivatedMatchCount === 1 ? 'account matches' : 'accounts match'} your search.
                        {' '}<Typography component='span' sx={{ fontSize: 'inherit', color: 'primary.main', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => setFilter(f => ({ ...f, incInactive: true }))}>Show deactivated accounts</Typography>
                    </Alert>
                )}

                {view === 'list' && (
                    <AccountsMainList mode={mode} list={scoreFilteredAccounts} opportunities={filteredOpportunities} onOpen={onOpen} selected={selected} setSelected={setSelected} featureToggle={featureToggle} accounts={accounts} suppliers={suppliers} accountScores={accountScores} accountScorer={accountScorer} />
                )}
                {view === 'kanban' && (
                    <AccountsMainKanban mode={mode} org={org} me={me} accounts={scoreFilteredAccounts} opportunities={filteredOpportunities} onOpen={onOpen} updateAccount={updateAccount} showOverdue={showOverdue} setShowOverdue={setShowOverdue} sorting={sorting} accountScores={accountScores} accountScorer={accountScorer} hideNewStage={featureToggle?.['feature.accounts-hide-new'] && mode === 'accounts' && !includeNew} />
                )}

                {showSendEmail && (
                    <Dialog open={showSendEmail} onClose={() => setShowSendEmail(false)} maxWidth={'none'}>
                        <SendNewEmailDialog
                            org={org}
                            accesstoken={accesstoken}
                            accounts={accounts}
                            onClose={() => setShowSendEmail(false)}
                            onSent={() => {}}
                            emailLoginInfo={emailLoginInfo}
                            toRecipients={selected.map(id => {
                                const account = accounts.find(a => a.id === id);
                                const contact = account.contacts?.[0];
                                return {
                                    name: contact?.firstName ? `${contact.firstName} ${contact.lastName || ''}` : account.name,
                                    address: contact?.email || account.email,
                                }
                            })}
                        />
                    </Dialog>
                )}

                {showCreateAudience && (
                    <Dialog open={showCreateAudience} onClose={() => setShowCreateAudience(false)} maxWidth={'lg'} fullWidth>
                        <Box sx={{ display: 'flex', alignItems: 'center', padding: '12px 24px' }}>
                            <Typography variant='h6' sx={{ flex: 1 }}>
                                Create a New Audience
                            </Typography>
                            <IconButton color='error' onClick={() => setShowCreateAudience(false)}>
                                <FontAwesomeIcon icon={faXmark} />
                            </IconButton>
                        </Box>
                        <Typography variant='body1' component={Box} sx={{ padding: '12px 24px' }}>
                            Create here new audience for your emails, follow-ups, reminders, and campaigns.  Your list of accounts is auto-updated, with new accounts matching the criteria added constantly.
                        </Typography>
                        <Box sx={{ padding: '12px 24px' }}>
                            <AudienceDetailsInner initialAccounts={scoreFilteredAccounts} initialFilter={filter} allAccounts={accounts} seaPorts={seaPorts} />
                        </Box>
                        <DialogActions>
                            <Button color='primary' variant='contained' onClick={() => setShowCreateAudience(false)}>
                                Create Audience
                            </Button>
                        </DialogActions>

                    </Dialog>
                )}
            </Box>
            <NewTaskDialog open={showNewTaskDialog} onClose={() => setShowNewTaskDialog(false)} accounts={data} org={org} me={me} accesstoken={accesstoken} setAccounts={setAccounts} />
        </Box>
    );
}

export default AccountsMain;

